import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type DamageLocations =
  | 'Rear Driver Corner'
  | 'Rear Driver Fender'
  | 'Driver Side Door(s)'
  | 'Front Driver Fender'
  | 'Front Driver Corner'
  | 'Rear'
  | 'Trunk'
  | 'Roof'
  | 'Hood'
  | 'Front'
  | 'Rear Passenger Corner'
  | 'Rear Passenger Fender'
  | 'Passenger Side Door(s)'
  | 'Front Passenger Fender'
  | 'Front Passenger Corner';

export interface DamageLocationAction {
  type: DamageLocations;
  damaged: boolean;
}

interface DamageLocationState {
  damageLocation: Record<DamageLocations, boolean>;
  hasFrontEndDamage: boolean;
  hasRearEndDamage: boolean;
  hasSideDamage: boolean;
}

function isFrontEndDamage(type: DamageLocations): boolean {
  return (
    ['Front', 'Front Driver Fender', 'Front Driver Corner', 'Front Passenger Fender', 'Front Passenger Corner'] as DamageLocations[]
  ).includes(type);
}

function isRearEndDamage(type: DamageLocations): boolean {
  return (
    ['Rear', 'Rear Driver Corner', 'Rear Driver Fender', 'Rear Passenger Fender', 'Rear Passenger Corner'] as DamageLocations[]
  ).includes(type);
}

function isSideDamage(type: DamageLocations): boolean {
  return type === 'Driver Side Door(s)' || type === 'Passenger Side Door(s)';
}

const initialState: DamageLocationState = {
  damageLocation: {
    'Rear Driver Corner': false,
    'Rear Driver Fender': false,
    'Driver Side Door(s)': false,
    'Front Driver Fender': false,
    'Front Driver Corner': false,
    'Rear': false,
    'Trunk': false,
    'Roof': false,
    'Hood': false,
    'Front': false,
    'Rear Passenger Corner': false,
    'Rear Passenger Fender': false,
    'Passenger Side Door(s)': false,
    'Front Passenger Fender': false,
    'Front Passenger Corner': false,
  },
  hasFrontEndDamage: false,
  hasRearEndDamage: false,
  hasSideDamage: false,
};

const damageLocationsSlice = createSlice({
  name: 'damageLocations',
  initialState,
  reducers: {
    setDamageLocation: (state, { payload }: PayloadAction<DamageLocationAction>) => {
      state.damageLocation[payload.type] = payload.damaged;
      if (isFrontEndDamage(payload.type)) {
        state.hasFrontEndDamage = (Object.keys(state.damageLocation) as DamageLocations[])
          .filter(k => isFrontEndDamage(k))
          .some(k => state.damageLocation[k]);
      } else if (isSideDamage(payload.type)) {
        state.hasSideDamage = (Object.keys(state.damageLocation) as DamageLocations[])
          .filter(k => isSideDamage(k))
          .some(k => state.damageLocation[k]);
      } else if (isRearEndDamage(payload.type)) {
        state.hasRearEndDamage = (Object.keys(state.damageLocation) as DamageLocations[])
          .filter(k => isRearEndDamage(k))
          .some(k => state.damageLocation[k]);
      }
    },
  },
});

export const { setDamageLocation } = damageLocationsSlice.actions;

export default damageLocationsSlice.reducer;

